import React, { useEffect, useState, useMemo } from "react";
import * as S from "./MessageDisplay.style";
import { useTranslation } from "react-i18next";
import Popup from "reactjs-popup";
import { Formik } from "formik";
import * as Yup from "yup";

// Components
import ZaiaImage from "../../../../zaiaComponents/zaiaImage";
import Message from "../../../algorithms/components/message";
import NoPatients from "../../../shared/noPatients";
import ZaiaReaction from "../../../../zaiaComponents/zaiaReaction";

// graphQL
import { useQuery, useLazyQuery, useMutation, useSubscription } from "@apollo/client";
import { QUERY_GET_MESSAGES } from "../../../../../graphql/queries/Chat";
import { MUTATION_CHAT_MESSAGES } from "../../../../../graphql/mutations/Chat";

// Pipe
import * as P from "../../../../../utils/pipes"

//Hooks
import useInfoToken from "../../../../../hooks/useInfoToken";
import { SUBSCRIPTION_GET_LAST_MESSAGE } from "../../../../../graphql/subscriptions/Chat";
import { useRef } from "react";
import { useLayoutEffect } from "react";
import useDoctorData from "../../../../../hooks/useDoctorData";
import useUploadDoc from "../../../../../hooks/useUploadDoc";
import useUploadTempDoc from "../../../../../hooks/useUploadTempDoc";

// Assets
const logoZaiaLoading = `${process.env.REACT_APP_STORAGE_ZAIA_RESOURCES}/zaiaReactions/singularity-health-loader.svg`;
const logoZaiaAlert = `${process.env.REACT_APP_STORAGE_ZAIA_RESOURCES}/zaiaReactions/reaction-error-data.svg`;
const logoZaiaEmpty = `${process.env.REACT_APP_STORAGE_ZAIA_RESOURCES}/zaiaReactions/reaction-no-data-loaded.svg`;
const defaultChatGroup = `${process.env.REACT_APP_STORAGE_ZAIA_RESOURCES}/zaiaImgDefault/default-chat-group.png`;
const BUCKET_CHAT_FILES = process.env.REACT_APP_BUCKET_CHAT_FILES;
const UPLOAD_GUIDE_PATIENT_ENDPOINT = process.env.REACT_APP_PATIENT_GUIDE_MEDPALM_ENDPOINT;

function MessageDisplay({
  selectedChat,
  groupChatsData,
  groupChatsLoading,
  groupChatsError,
  setSettingsGroup,
  setMedicineSettings,
  medicineMessage,
  setMedicineMessage,
  setChatActive,
  setSelectedChat
}) {
  const {t, i18n: { language }} = useTranslation("global");
  const [allMessages, setAllMessages] = useState([]);
  const [currentChat, setCurrentChat] = useState();
  const [offset, setOffset] = useState(20);
  const { userId } = useInfoToken();
  const { doctorData } = useDoctorData();
  const fileInput = useRef(null);
  const {tempDoc, dataDoc, errorFormat, uploadTempDoc, deleteDoc} = useUploadTempDoc();
  const {uploadDoc, data, loading, error, cleanAction} = useUploadDoc();  
  const [sendingFile, setSendingFile] = useState(false);

  const handleClick = (e) => {    
    fileInput.current.click();
  }

  // const uplodPatientGuide = async () => {
  //     try {
  //       await saveDoc({
  //         variables: {
  //           collection_name: "FLORENCE_PATIENT_MEDICAL_HISTORY",
  //           data_source_id: "PDF",
  //           data_source_tag: "PDF",
  //           embedding_model: "textembedding-gecko-multilingual@001",
  //           embedding_technology: "google",
  //           file_bucket_name: uploaded_file_data[0].file_bucket_name,
  //           file_name: uploaded_file_data[0].file_name,
  //           model_vector_dimension: 768,
  //           user_id: ID_STANDARD_PATIENT_GUIDE_MEDPALM
  //         },
  //       });
  //       setUploadDocResponse(true);
  //       deleteDoc()
  //       fileInput.current.value = null;
  //     } catch (err) {
  //       setUploadDocResponse(false);
  //       deleteDoc();
  //       fileInput.current.value = null;
  //     }
  // }

  useEffect(() => {
    if(currentChat?.id !== selectedChat?.id) {
      setCurrentChat(selectedChat);
      setAllMessages([]);
      setOffset(20);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChat, currentChat, setCurrentChat])

  useEffect(() => {
    if(medicineMessage !== '') {
      handleSubmitMessageType(medicineMessage);
      setMedicineMessage('')
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medicineMessage])

  const [sendMessage, { data: sendMessageData, loading: sendMessageLoading, error: sendMessageError }] = useMutation(MUTATION_CHAT_MESSAGES);

  // Query get past messages with offset
  const [
    getMessages,
    {
      loading: oldMessagesLoading
    }
  ] = useLazyQuery(
    QUERY_GET_MESSAGES,
    {
      onCompleted: (data) => {
        setAllMessages(prevState => [...prevState, ...data.message])
      },
      fetchPolicy: "network-only"
    }
  );

  // Query get first last 20 messages
  const {
    loading: lastMessagesloading,
  } = useQuery(QUERY_GET_MESSAGES,
    {
      variables: { groupChatId: selectedChat?.id },
      onCompleted: (data) => {
        setAllMessages([]);
        setAllMessages(prevState => [...prevState, ...data.message]);
      },
      fetchPolicy: "network-only"
    }
  );

  // Subscription last message
  useSubscription(
      SUBSCRIPTION_GET_LAST_MESSAGE,
    {
      variables: { 
        doctorId: doctorData?.id,
       },
      onData: ({data: {data}}) => {        
        const newMessage = data.message[0];      

        if (allMessages?.findIndex(msg => msg.id === newMessage?.id) === -1 && newMessage?.chatGroup.id === selectedChat?.id) {
          setAllMessages(prevState => [...data.message, ...prevState])
          setOffset(offset + 1);
        }
      }
    }
  );

  const getOldMessages = () => {
    getMessages({ variables: { groupChatId: selectedChat?.id, offset: offset }});
    setOffset(offset + 20);
  }

  const handleSubmitMessageType = async (newMessage) => {
    try {
      const {
        data: {
          insert_message: { returning }
        }
      } = await sendMessage({
        variables: {
          message: newMessage,
          groupChatId: selectedChat?.id,
        },
      });
      if (allMessages?.findIndex(msg => msg.id === returning[0].id) === -1) {
        setAllMessages(prevState => [...returning, ...prevState])
        setOffset(offset + 1);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setSendingFile(true);
    try {
      if(resetForm) resetForm();
      if(setSubmitting) setSubmitting(false);
      let uploaded_file = null;
      if(tempDoc || dataDoc) {
        let { data: { uploaded_file_data } } = await uploadDoc(tempDoc, UPLOAD_GUIDE_PATIENT_ENDPOINT, BUCKET_CHAT_FILES);
        uploaded_file = uploaded_file_data[0].file_url;
      }

      console.log("German", 
      tempDoc?.type);

      console.log("sixzeronine", 
      tempDoc?.type.includes("pdf"));
      
      
      const {
        data: {
          insert_message: { returning }
        }
      } = await sendMessage({
        variables: {
          message: values.msg ? values.msg : "",
          groupChatId: selectedChat?.id,
          urlFile: uploaded_file,
          attachment: tempDoc 
            ? (
              tempDoc.type?.includes("image") 
              ? "image" 
              : tempDoc.type?.includes("pdf") 
                ? "pdf"
                : "other"
            ) 
            : null,
        }
      });
      
      if (allMessages?.findIndex(msg => msg.id === returning[0].id) === -1) {
        setAllMessages(prevState => [...returning, ...prevState])
        setOffset(offset + 1);
        deleteDoc();
      }

      setSendingFile(false);
    } catch (error) {
      console.log(error);
      setSendingFile(false);
    }
  };

  const handleScroll = (e) => {
    const bottom = e.target.clientHeight - e.target.scrollHeight >= e.target.scrollTop - 2;
    if (bottom && !oldMessagesLoading) {
      getOldMessages();
     }
  }

  const messagesFilter = useMemo(() => {
    let messages = [];
    allMessages?.forEach(msg => {

      let option = 'receiver';
      if(msg.fromDoctorId === userId) option = 'doctor'
      let senderName;

      senderName = msg?.fromUserId ?
        `${msg.user?.name + " " + msg.user?.lastname}` :
        `${P.drTranslation(msg.doctor?.gender, language)} ${msg.doctor?.name + " " + msg.doctor?.lastname}`

      const time = P.hoursIn12format(new Date(msg.createdAt).getHours(), new Date(msg.createdAt).getMinutes());
      const date = P.shortDate(new Date(msg.createdAt), t, true, language);
      messages.push(
        {
          key: msg.id,
          message: msg.message,
          option: option,
          time: time,
          date: date,
          senderName: senderName,
          file: msg.urlFile
        }
      )
    });
    return messages;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allMessages])



  if(groupChatsLoading || lastMessagesloading)
    return (
      <>
        <ZaiaReaction
          widthimg="150px"
          zaiaImg={logoZaiaLoading}
        />
      </>
    );

  if(groupChatsError)
    return (
      <>
        <ZaiaReaction
          zaiaImg={logoZaiaAlert}
          widthimg="100px"
          text={t("chat.errorLoadingChats")}
          sizeText="15px"
          widthText="240px"
        />
      </>
    );

  if (!selectedChat?.id && groupChatsData?.chatGroup.length === 0)
    return (
      <>
        <NoPatients
          title={t("dashboard.boxes.noAlerts")}
          img={logoZaiaEmpty}
        />
      </>
    );

  if(sendingFile) 
    return (
      <S.MessageDisplayDiv id="messageDisplay">
        <ZaiaReaction
          zaiaImg={logoZaiaLoading}
          widthimg="150px"
        />
      </S.MessageDisplayDiv>
    );
  

  return (
    <S.MessageDisplayDiv id="messageDisplay">
      <input
        type="file"
        ref={fileInput}
        onChange={(e) => uploadTempDoc(e)}
        style={{display: 'none'}}
        accept="image/*,.pdf"  
      />
      {
        !tempDoc
        ? <>
          <header className="messageDisplay__header">
            <div className="messageDisplay__headerName">
              <div className="messageDisplay__headerBack" onClick={() => {setChatActive(false); setSelectedChat(null)}}>
                <i className="send-icon icon zaia-icono-atras"></i>
              </div>
              <div className="oneSingleChat__imgContainer">
                <ZaiaImage img={selectedChat?.chatPhotoUser} alt="Zaia user" size={40} defaultImg={defaultChatGroup}/>
                {/* <div className="oneSingleChat__online"></div> */}
              </div>
              <h4>{selectedChat?.chatNameUser}</h4>
            </div>
            <Popup
              trigger={
                <div className="icon-container">
                  <div className="icon zaia-icono-dots-horizontal"></div>
                </div>
              }
              keepTooltipInside
              nested
            >
              { close =>
                <ChatSettings setSettingsGroup={setSettingsGroup} close={close}/>
              }
            </Popup>
          </header>
          <div className="rightside__container-msgs">
            <div className="rightside__msgs" onScroll={handleScroll}>
              {messagesFilter.map((message, index) => {
                const nextMessage = messagesFilter[index + 1];
                const showSenderName = !nextMessage || nextMessage.option !== message.option;                
                return (
                  <Message
                    key={message.key}
                    message={message.message}
                    option={message.option}
                    time={message.time}
                    date={message.date}
                    senderName={showSenderName || selectedChat.isGroup === true? message.senderName : null}
                    file={message.file}
                  />
                );
              })}
            </div>
          </div>
        </>
        : <div className="file-selected">
          <div className="header">
            <div className="header__close"
              onClick={() => {
                deleteDoc();
                fileInput.current.value = null;
                cleanAction();
              }}
            >
              <i className="icon zaia-icono-cerrar"></i>
            </div>
            <div className="header__info">
              <div className="header__img">
                <ZaiaImage img={selectedChat?.chatPhotoUser} alt="Zaia user" size={40} defaultImg={defaultChatGroup} rounded={true}/>
              </div>
              <div className="header__text">
                <div className="text">
                  {selectedChat?.chatNameUser}
                </div>
                <div className="text">
                  {selectedChat?.userToProcess ? P.dataTranslation(selectedChat?.userToProcess.process?.processName, language) : ""}
                </div>
              </div>
            </div>
          </div>
          {
            error ?
            <div className="error-reaction">
              <ZaiaReaction
                zaiaImg={logoZaiaAlert}
                widthimg="100px"
                text={t("chat.errorLoadingFile")}
                sizeText="20px"
                widthText="240px"
              />
            </div>
            : <div className="file">
              <div className="file__preview">
                {
                  tempDoc?.type?.includes("image") 
                  ? <div className="file__img">
                    <img src={
                      tempDoc?.url || URL.createObjectURL(tempDoc)
                    } alt="" />
                  </div>
                  : <div className="file__icon">
                    <i className="icon zaia-icon-file"></i>
                  </div>
                }
              </div>
              <div className="file__title">
                {tempDoc?.name || dataDoc?.name || "Archivo"}
              </div>
            </div>
          }
        </div>
      }
      <Formik
        initialValues={{
          msg: "",
        }}
        validationSchema={
          Yup.object({
            msg: !tempDoc && Yup.string()
            .required(),
          })
        }
        onSubmit={handleSubmit}
      >
        {(props) => (
          <form
            onSubmit={props.handleSubmit}
            className="messageDisplay__containerFooter"
          >
            <Popup
              trigger={
                <button className="send-icon-container" type="button" disabled={props.isSubmitting}>
                  <i className="send-icon icon zaia-icono-agregar"></i>
                </button>
              }
              // keepTooltipInside="#messageDisplay"
              keepTooltipInside
              position="top center"
              // nested
            >
              { 
                close =>
                <S.MessagesTypes>
                  <div className="chat-setting-box" onClick={() => {
                    setMedicineSettings(true); 
                    close()
                  }}>
                    <div className="chat-setting-box__text">
                      {t("aiNavigator.patientModal.sendMedications")}
                    </div>
                    <i className="icono-settings zaia-icono-medicina"></i>
                  </div>
                  <div className="chat-setting-box chat-setting-box--file" onClick={() => {
                    handleClick();
                    close()
                  }}>
                    <div className="chat-setting-box__text">
                      {t("globally.sendFile")}
                    </div>
                    <i className="icono-settings zaia-i-upload"></i>
                  </div>
                </S.MessagesTypes>
              }
            </Popup>
            <input
              placeholder={t("globally.writeMessage")}
              name="msg"
              type="text"
              onChange={props.handleChange}
              onBlur={props.handleBlur}
              value={props.values.msg}
              autoComplete="off"
            />
            <button type="submit" disabled={props.isSubmitting}>
              <i className="send-icon icon zaia-send send-icon--big"></i>
            </button>
          </form>
        )}
      </Formik>
    </S.MessageDisplayDiv>
  );
}

const ChatSettings = ({setSettingsGroup, close}) => {
 const [t] = useTranslation("global");
  return (
    <S.ChatSettings>
      <div className="chat-setting-box chat-setting-box__medicine" onClick={() => {setSettingsGroup(true); close()}}>
        <i className="icono-settings zaia-alert"></i>
        {t("chat.settingsChat.info")}
      </div>
    </S.ChatSettings>
  )
}


export default MessageDisplay;
